import { useQueries } from "@tanstack/react-query";
import { ALL, NFL } from "@/constants/sports";
import {
  GetActiveFixturesQuery,
  GetActiveFixturesNflQuery,
} from "@/graphql/gql/graphql";
import { getScores, getScoresNfl } from "../api";
import { normalizeNflFixture } from "../scores";

/**
 * The current `getActiveFixtures` backend logic does not fully support NFL year-round.
 * Sizeable refactoring of the backend is needed to address this limitation.
 *
 * As an interim solution, NFL scores are fetched using a separate `getNflSchedule` query,
 * while MLB, NBA, and NHL scores continue to use `getActiveFixtures`.
 *
 * This hook retrieves scores for all sports, including NFL, and returns them in a normalized
 * format for use in the same GameCard component.
 */

const refetchInterval = 30000; // 30 seconds in ms
const useGetScores = (sport = ALL) => {
  const sportArg = sport === ALL ? undefined : sport;
  const [scoresNflQuery, scoresQuery] = useQueries({
    queries: [
      {
        queryKey: ["getActiveFixturesNfl", NFL],
        queryFn: () => getScoresNfl(),
        refetchInterval,
        select: (data: GetActiveFixturesNflQuery) => data?.getNflSchedule,
      },
      {
        queryKey: ["getActiveFixtures", sportArg],
        queryFn: () => getScores(sportArg),
        refetchInterval,
        select: (data: GetActiveFixturesQuery) => data?.getActiveFixtures,
        enabled: sportArg !== NFL,
      },
    ],
  });

  // Normalize NFL scores so GameCard can consume the same data structure
  const normalizedNflScores = (scoresNflQuery?.data ?? []).map((score) =>
    normalizeNflFixture(score)
  );

  // If all sports, combine NFL scores with all other scores and sort by time
  const allScores = [
    ...(normalizedNflScores ?? []),
    ...(scoresQuery.data ?? []),
  ].sort(
    (a, b) =>
      new Date(a?.scheduledStartTime ?? 0).getTime() -
      new Date(b?.scheduledStartTime ?? 0).getTime()
  );

  switch (sport) {
    case NFL:
      return {
        scores: normalizedNflScores,
        isLoading: scoresNflQuery.isLoading,
        isError: scoresNflQuery.isError,
      };
    case ALL:
      return {
        scores: allScores,
        isLoading: scoresNflQuery.isLoading || scoresQuery.isLoading,
        isError: scoresNflQuery.isError || scoresQuery.isError,
      };
    default:
      return {
        scores: scoresQuery.data || [],
        isLoading: scoresQuery.isLoading,
        isError: scoresQuery.isError,
      };
  }
};

export default useGetScores;
